import type { TrafficControlDataLayerDisplayConditionsItem } from '../../types/configs.generated';
import { logger } from '../core/logger';

type DataLayerDisplayCondition = TrafficControlDataLayerDisplayConditionsItem;

interface DataLayerWindow extends Window {
    dataLayer?: unknown[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function mergeRecord(
    target: Record<string, unknown>,
    source: Record<string, unknown>
): Record<string, unknown> {
    for (const [key, value] of Object.entries(source)) {
        const existing = target[key];
        target[key] =
            isRecord(existing) && isRecord(value) ? mergeRecord({ ...existing }, value) : value;
    }
    return target;
}

export function snapshotDataLayer(dataLayer: unknown[] | undefined): Record<string, unknown> {
    const snapshot: Record<string, unknown> = {};
    if (!Array.isArray(dataLayer)) return snapshot;

    for (const entry of dataLayer) {
        if (isRecord(entry)) {
            mergeRecord(snapshot, entry);
        }
    }

    return snapshot;
}

function readPath(source: Record<string, unknown>, path: string): unknown {
    let current: unknown = source;
    for (const part of path.split('.')) {
        if (!part || !isRecord(current)) return undefined;
        current = current[part];
    }
    return current;
}

function valueMatches(actual: unknown, allowedValues: Set<string>): boolean {
    if (Array.isArray(actual)) {
        return actual.some((item) => valueMatches(item, allowedValues));
    }
    if (typeof actual === 'string' || typeof actual === 'number' || typeof actual === 'boolean') {
        return allowedValues.has(String(actual));
    }
    return false;
}

function conditionMatches(
    condition: DataLayerDisplayCondition,
    snapshot: Record<string, unknown>
): boolean {
    const key = condition.key?.trim();
    const values = condition.values?.filter((value) => value.length > 0) ?? [];
    if (!key || values.length === 0) {
        logger.warn('Data layer display condition is invalid; blocking widget display', {
            condition,
        });
        return false;
    }

    return valueMatches(readPath(snapshot, key), new Set(values));
}

export function isDataLayerAllowed(
    conditions: DataLayerDisplayCondition[] | null | undefined,
    dataLayer: unknown[] | undefined = typeof window !== 'undefined'
        ? (window as DataLayerWindow).dataLayer
        : undefined
): boolean {
    if (!conditions || conditions.length === 0) return true;

    const snapshot = snapshotDataLayer(dataLayer);
    const allowed = conditions.every((condition) => conditionMatches(condition, snapshot));

    if (!allowed) {
        logger.debug('Data layer display conditions blocked widget display', {
            conditions,
            keys: Object.keys(snapshot),
        });
    }

    return allowed;
}
